import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { callscreeningQueryOptions, testCallscreeningFn, updateRuleSetFn } from "@/server/callscreening";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/form/form-context";
import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_dashboard/callscreening/system")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Call Screening",
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const systemRuleSetQuery = useQuery(callscreeningQueryOptions);
  const systemRuleSet = systemRuleSetQuery.data || {};
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (!systemRuleSet) return;
    void systemRuleSetQuery.refetch();
  }, [systemRuleSet, systemRuleSetQuery.refetch]);

  const testCallscreeningMutation = useMutation({
    mutationFn: testCallscreeningFn,
    onSuccess: (data) => {
      console.log("testCallscreeningMutation success!", data);
    },
    onError: (error) => {
      console.log("testCallscreeningMutation error!", error);
    },
  });

  const testCallScreening = (payload: any) => {
    const testCallscreeningPromise = testCallscreeningMutation.mutateAsync({ data: { payload } });
    toast.promise(testCallscreeningPromise, {
      loading: "Testing callscreening...",
      success: (data) =>
        `Call is ${data.isAllowed ? "allowed" : "blocked"} ${data.aNumber ? `A number: ${data.aNumber}` : ""} ${data.bNumber ? `B number: ${data.bNumber}` : ""}`,
      error: (error) => error.message || "Failed to test callscreening",
    });
  };

  const testFieldsForm = useAppForm({
    defaultValues: {
      ip: "",
      aNumber: "",
      userAgent: "",
      bNumber: "",
      rules: {},
    },
    onSubmit: (formData) => {
      if (formData.meta) {
        const payload = {
          ...formData.value,
        };
        const rules = form.state.values[formData.meta["event"]];
        payload.rules = rules;
        testCallScreening(payload);
      }
    },
  });

  const clearFormValues = () => {
    form.setFieldValue("incoming", { modify: "", black: "", white: "" });
    form.setFieldValue("outgoing", { modify: "", black: "", white: "" });
  };

  const updateRuleSetMutation = useMutation({
    mutationFn: updateRuleSetFn,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["callscreening"] });
      form.reset(data.ruleSet);
    },
    onError: (error) => {
      console.log("updateRuleSetMutation error!", error);
    },
  });

  const updateRuleSet = (ruleSet: any) => {
    const payload = {};
    Object.keys(ruleSet).forEach((key) => {
      payload[key] = {};
      Object.keys(ruleSet[key]).forEach((subKey) => {
        if (systemRuleSet[key][subKey] !== ruleSet[key][subKey]) {
          payload[key][subKey] = ruleSet[key][subKey];
        }
      });
      if (Object.keys(payload[key]).length === 0) {
        delete payload[key];
      }
    });
    const updateRuleSetPromise = updateRuleSetMutation.mutateAsync({ data: { ruleSetId: "system", payload } });
    toast.promise(updateRuleSetPromise, {
      loading: "Updating rule set...",
      success: "Rule set updated successfully",
      error: (error) => error.message || "Failed to update rule set",
    });
  };

  const formDefaultValues = useMemo(
    () => ({
      incoming: {
        modify: systemRuleSet.incoming?.modify ?? "",
        black: systemRuleSet.incoming?.black ?? "",
        white: systemRuleSet.incoming?.white ?? "",
      },
      outgoing: {
        modify: systemRuleSet.outgoing?.modify ?? "",
        black: systemRuleSet.outgoing?.black ?? "",
        white: systemRuleSet.outgoing?.white ?? "",
      },
    }),
    [systemRuleSet]
  );

  const form = useAppForm({
    defaultValues: formDefaultValues,
    onSubmit: (formData) => {
      updateRuleSet(formData.value);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Default callscreening rules</h1>

      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground mb-4">
          Callscreening is a feature that allows you to screen calls before they are answered. It is used to block or
          allow calls based on the caller&apos;s number, IP address, and user agent. Note that rulesets don&apos;t
          overwrite the default rulesets, but rather add to them.
        </p>
        <Card>
          <CardHeader>
            <CardTitle>Test fields for current rules</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                testFieldsForm.handleSubmit();
              }}
            >
              <section className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <testFieldsForm.AppField
                    name="ip"
                    children={(field) => (
                      <field.FormTextField label="IP address" id="ipAddress" description="IP address to test." />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <testFieldsForm.AppField
                    name="aNumber"
                    children={(field) => (
                      <field.FormTextField label="A number" id="aNumber" description="A number to test." />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <testFieldsForm.AppField
                    name="userAgent"
                    children={(field) => (
                      <field.FormTextField label="User agent" id="userAgent" description="User agent to test." />
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <testFieldsForm.AppField
                    name="bNumber"
                    children={(field) => (
                      <field.FormTextField label="B number" id="bNumber" description="B number to test." />
                    )}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => testFieldsForm.handleSubmit({ event: "incoming" })}
                  >
                    Test incoming
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => testFieldsForm.handleSubmit({ event: "outgoing" })}
                  >
                    Test outgoing
                  </Button>
                </div>
              </section>
            </form>
          </CardContent>
        </Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <Card>
            <CardHeader>
              <CardTitle>Incoming rules</CardTitle>
            </CardHeader>
            <CardContent>
              <section className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="incoming.modify"
                    children={(field) => (
                      <field.FormTextArea
                        label="Modify"
                        id="incomingModify"
                        description="Modify rules consist of two parts separated with a '|' [Match]|[Modify]"
                      />
                    )}
                  />
                  <div className="text-sm">
                    Modify rules consist of two parts separated with a '|' [Match]|[Modify]
                    <br />
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*|A:F-1-+46
                        <br />
                        All A-Nr beginning with 070 will be modified to +4670...
                      </li>
                      <li>
                        B:.*@domain.com|B:B-11-@mydomain.com
                        <br />
                        All B-Nr ending with @domain.com will be modified to ...@mydomain.com
                      </li>
                      <li>
                        UA:.*android.*|B:R-.*@-androidQueue@
                        <br />
                        All calls with a user agent containing android will be modified to androidQueue@...
                      </li>
                      <li>
                        IP:us|B:R-.*-callfromus@mydomain.com
                        <br />
                        All calls from us will end up in callfromus@mydomain.com
                      </li>
                      <li>
                        A:.*user@domain.com|SD:service@mydomain.com
                        <br />
                        All A-Nr ending with user@domain.com will have a suggested b number service@theirdomain.com when
                        picked up by agent
                      </li>
                      <li>
                        B:0053.*|GW:CUBA
                        <br />
                        All calls with prefix 0053 will utilize gateway with property "CUBA"
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="incoming.black"
                    children={(field) => <field.FormTextArea label="Blacklist" id="incomingBlacklist" description="" />}
                  />
                  <div className="text-sm">
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*
                        <br />
                        All A-Nr beginning with 070 will be blocked.
                      </li>
                      <li>
                        B:.*@domain.com
                        <br />
                        All B-Nr ending with @domain.com will be blocked.
                      </li>
                      <li>
                        UA:.*IOS.*
                        <br />
                        All user agents containing IOS will be blocked.
                      </li>
                      <li>
                        IP:10.0.75.0/24
                        <br />
                        All calls from ip within the range 10.0.75.0-10.0.75.255 will be blocked.
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="incoming.white"
                    children={(field) => <field.FormTextArea label="Whitelist" id="incomingWhitelist" description="" />}
                  />
                  <div className="text-sm">
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*
                        <br />
                        All A-Nr beginning with 070 will be blocked.
                      </li>
                      <li>
                        B:.*@domain.com
                        <br />
                        All B-Nr ending with @domain.com will be blocked.
                      </li>
                      <li>
                        UA:.*IOS.*
                        <br />
                        All user agents containing IOS will be blocked.
                      </li>
                      <li>
                        IP:10.0.75.0/24
                        <br />
                        All calls from ip within the range 10.0.75.0-10.0.75.255 will be blocked.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Outgoing rules</CardTitle>
            </CardHeader>
            <CardContent>
              <section className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="outgoing.modify"
                    children={(field) => (
                      <field.FormTextArea
                        label="Modify"
                        id="outgoingModify"
                        description="Modify rules consist of two parts separated with a '|' [Match]|[Modify]"
                      />
                    )}
                  />
                  <div className="text-sm">
                    Modify rules consist of two parts separated with a '|' [Match]|[Modify]
                    <br />
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*|A:F-1-+46
                        <br />
                        All A-Nr beginning with 070 will be modified to +4670...
                      </li>
                      <li>
                        B:.*@domain.com|B:B-11-@mydomain.com
                        <br />
                        All B-Nr ending with @domain.com will be modified to ...@mydomain.com
                      </li>
                      <li>
                        UA:.*android.*|B:R-.*@-androidQueue@
                        <br />
                        All calls with a user agent containing android will be modified to androidQueue@...
                      </li>
                      <li>
                        IP:us|B:R-.*-callfromus@mydomain.com
                        <br />
                        All calls from us will end up in callfromus@mydomain.com
                      </li>
                      <li>
                        A:.*user@domain.com|SD:service@mydomain.com
                        <br />
                        All A-Nr ending with user@domain.com will have a suggested b number service@theirdomain.com when
                        picked up by agent
                      </li>
                      <li>
                        B:0053.*|GW:CUBA
                        <br />
                        All calls with prefix 0053 will utilize gateway with property "CUBA"
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="outgoing.black"
                    children={(field) => <field.FormTextArea label="Blacklist" id="outgoingBlacklist" description="" />}
                  />
                  <div className="text-sm">
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*
                        <br />
                        All A-Nr beginning with 070 will be blocked.
                      </li>
                      <li>
                        B:.*@domain.com
                        <br />
                        All B-Nr ending with @domain.com will be blocked.
                      </li>
                      <li>
                        UA:.*IOS.*
                        <br />
                        All user agents containing IOS will be blocked.
                      </li>
                      <li>
                        IP:10.0.75.0/24
                        <br />
                        All calls from ip within the range 10.0.75.0-10.0.75.255 will be blocked.
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <form.AppField
                    name="outgoing.white"
                    children={(field) => <field.FormTextArea label="Whitelist" id="outgoingWhitelist" description="" />}
                  />
                  <div className="text-sm">
                    Example:
                    <ul className="list-disc pl-4 mt-2 space-y-2">
                      <li>
                        A:070.*
                        <br />
                        All A-Nr beginning with 070 will be blocked.
                      </li>
                      <li>
                        B:.*@domain.com
                        <br />
                        All B-Nr ending with @domain.com will be blocked.
                      </li>
                      <li>
                        UA:.*IOS.*
                        <br />
                        All user agents containing IOS will be blocked.
                      </li>
                      <li>
                        IP:10.0.75.0/24
                        <br />
                        All calls from ip within the range 10.0.75.0-10.0.75.255 will be blocked.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>
            </CardContent>
          </Card>

          <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit]}>
            {([isSubmitting, isDirty, canSubmit]) => (
              <div className="flex gap-2 sticky bottom-0 border-t p-4 bg-background z-10 mt-4">
                <Button type="submit" disabled={isSubmitting || !isDirty || !canSubmit}>
                  {isSubmitting || updateRuleSetMutation.status === "pending" ? "Saving..." : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCancelDialogOpen(true);
                  }}
                  disabled={isSubmitting || !isDirty || !canSubmit || updateRuleSetMutation.status === "pending"}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting || updateRuleSetMutation.status === "pending"}
                  onClick={() => clearFormValues()}
                >
                  Clear all fields
                </Button>
              </div>
            )}
          </form.Subscribe>
        </form>
      </div>
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard changes"
        description="Are you sure you want to undo all unsaved changes? This will reset the form back to its initial state."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={() => {
          form.reset(formDefaultValues);
          setCancelDialogOpen(false);
        }}
        onCancel={() => {
          setCancelDialogOpen(false);
        }}
      />
    </div>
  );
}
